import type { FC } from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { useDismissAllNotificationsMutation } from '@/hooks/mutations/useNotificationsMutations'
import {
  selectActionRequiredCount,
  useNotificationFeed,
} from '@/hooks/queries/useNotificationFeedQuery'
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications'
import { getNotificationRoute } from '@/lib/notifications/notification-route'
import { cn } from '@/lib/utils'
import { useNotificationActions } from '@/store/notifications'
import { BellIcon, CheckIcon, CircleNotchIcon } from '@phosphor-icons/react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Notification,
  NOTIFICATION_ACTION_TYPES,
  NOTIFICATION_MENTION_TYPES,
} from '@scrambl/shared'

import type { NotificationFeedLane } from './NotificationFeedPanel'
import { NotificationFeedPanel } from './NotificationFeedPanel'

interface NotificationCenterProps {
  className?: string
}

export const NotificationCenter: FC<NotificationCenterProps> = ({
  className,
}) => {
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [isOpen, setIsOpen] = useState(false)

  // The feed drives both the badge and the dropdown; it stays fresh via
  // socket events, so this is one cached request rather than per-open fetches.
  const { data: feed, isLoading } = useNotificationFeed()
  const {
    markNotificationAsRead,
    markAllNotificationsAsRead,
    markMultipleAsRead,
    dismissNotificationById,
    dismissMultiple,
  } = useNotificationActions()
  const dismissAllMutation = useDismissAllNotificationsMutation()
  const { clearBadgeCount, isDesktopApp } = useBrowserNotifications()

  const handleDismissLane = (lane: NotificationFeedLane) => {
    dismissAllMutation.mutate(
      lane === 'action'
        ? NOTIFICATION_ACTION_TYPES
        : NOTIFICATION_MENTION_TYPES,
    )
  }

  // One number, one meaning: the badge counts the Action required lane below
  // it and nothing else — the same figure the Today briefing quotes. Any other
  // unread (mentions, plain activity) is real but not a decision, so it gets a
  // quiet dot rather than a competing count.
  const actionCount = selectActionRequiredCount(feed)
  const mentionCount = feed?.counts.mentions ?? 0
  const unreadCount = feed?.counts.unread ?? 0
  const hasQuietUnread = mentionCount > 0 || unreadCount > 0

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open && isDesktopApp) {
      clearBadgeCount()
    }
  }

  const handleSelect = async (notification: Notification) => {
    if (notification.status === 'UNREAD') {
      void markNotificationAsRead(notification.id)
    }

    setIsOpen(false)

    // Burnout risk transition — navigate to Team Workload (insights tab)
    if (notification.type === 'BURNOUT_RISK_TRANSITION') {
      const memberId = notification.data?.memberId
      navigate(
        memberId
          ? `/${orgSlug}/team/${memberId}/workload`
          : `/${orgSlug}/team?tab=insights`,
      )
      return
    }

    const route = getNotificationRoute(orgSlug, notification)
    if (route) {
      navigate(route)
    }
  }

  const handleSelectGroup = (notifications: Notification[]) => {
    const unreadIds = notifications
      .filter(notification => notification.status === 'UNREAD')
      .map(notification => notification.id)
    if (unreadIds.length > 0) void markMultipleAsRead(unreadIds)
    setIsOpen(false)
    navigate(`/${orgSlug}/timesheets`)
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
          // A span's aria-label isn't announced; name the button itself.
          aria-label={
            actionCount > 0
              ? `Notifications, ${actionCount} ${actionCount === 1 ? 'needs' : 'need'} a decision`
              : hasQuietUnread
                ? 'Notifications, unread'
                : 'Notifications'
          }
        >
          <BellIcon className="h-5 w-5" aria-hidden />
          {actionCount > 0 ? (
            <span
              className={cn('nfd-badge', 'nfd-badge-action')}
              aria-label={`${actionCount} ${actionCount === 1 ? 'notification needs' : 'notifications need'} a decision`}
            >
              {actionCount > 99 ? '99+' : actionCount}
            </span>
          ) : hasQuietUnread ? (
            <span
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
              aria-label="Unread notifications"
            />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="end" sideOffset={5}>
        <div className="flex items-center justify-between p-4 pb-3">
          <h3 className="text-[0.71875rem] font-bold uppercase tracking-[0.11em] text-muted-foreground">
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllNotificationsAsRead()}
              className="h-8 px-3 text-xs"
            >
              <CheckIcon className="mr-1.5 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>

        <Separator />

        <div
          style={{
            maxHeight: 440,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
          onWheel={e => e.stopPropagation()}
        >
          {isLoading && !feed ? (
            <div className="flex h-32 items-center justify-center">
              <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : feed ? (
            <NotificationFeedPanel
              feed={feed}
              onSelect={handleSelect}
              onDismiss={notification =>
                void dismissNotificationById(notification.id)
              }
              onSelectGroup={handleSelectGroup}
              onDismissGroup={notifications =>
                void dismissMultiple(notifications.map(item => item.id))
              }
              onDismissLane={handleDismissLane}
              maxPerLane={4}
              className="pb-2"
            />
          ) : (
            <div className="nfd-empty">Unable to load notifications</div>
          )}
        </div>

        <Separator />

        <div className="flex items-center justify-between p-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-primary hover:text-primary"
            onClick={() => {
              setIsOpen(false)
              navigate(`/${orgSlug}/notifications`)
            }}
          >
            Open notifications
          </Button>
          {(feed?.resolvedToday ?? 0) > 0 && (
            <span className="nfd-resolved-note pr-2">
              {feed?.resolvedToday} auto-resolved today
            </span>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
